import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { createDeclaration, deleteDeclaration, submitDeclaration } from "../../../api/declarations";
import DeclarationContext from "../../../context/declaration/DeclarationContext";
import ThemeContext from "../../../context/theme/ThemeContext";
import { removeQueryParam, updateQueryParam } from "../../../router/utils";
import NotificationToast from "../../common/NotificationToast";
import CancelDeclarationModal from "../../modals/CancelDeclarationModal";
import Button from "../../common/Button";
import { PiArrowFatUp, PiCopy, PiFloppyDisk, PiPencilSimple, PiTrash, PiX } from "react-icons/pi";
import {
	AMENDMENT_QUERY_PARAM,
	CREATION_TIME_QUERY_PARAM,
	ELEMENT_QUERY_PARAM,
	PAGE_QUERY_PARAM,
} from "../../../constants/GlobalConstants";
import ConfirmationModal from "../../modals/ConfirmationModal";
import Badge from "../../common/Badge";

const DeclarationActions = (props) => {
	const {
		state,
		getValidationErrors,
		listDeclarations,
		setDeclarations,
		createAmendment,
		cancelAmendment,
		updateDeclaration,
		clearSelectedDeclaration
	} = useContext(DeclarationContext);
	const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen] = useState(false);
	const [isCancelConfirmModalOpen, setIsCancelConfirmModalOpen] = useState(false);
	const { withTheme } = useContext(ThemeContext);
	const { t } = useTranslation();
	const [toast, setToast] = useState("");
	const { setLoadingAction, loadingAction, isAmendment } = props;

	const handleAmendClick = () => {
		updateQueryParam(PAGE_QUERY_PARAM, "0");
		updateQueryParam(AMENDMENT_QUERY_PARAM, "true");
		createAmendment();
	};

	const handleCancelAmendmentClick = () => {
		updateQueryParam(AMENDMENT_QUERY_PARAM, "");
		updateQueryParam(PAGE_QUERY_PARAM, "0");
		cancelAmendment();
	};

	/**
	 * updating declaration data per logged in user
	 */
	const handleSaveClick = async (newStatus, amend) => {
		if (loadingAction) {
			return;
		}

		updateQueryParam(ELEMENT_QUERY_PARAM, "");
		setLoadingAction(amend ? "amend" : "save");

		try {
			await updateDeclaration(
				state.declarationMetadata.creationTime,
				{
					status:
						typeof newStatus === "string"
							? newStatus
							: state.declarationMetadata.status,
				},
				state.declaration,
				amend
			);

			// TODO: Avoid re-listng all declarations when save is clicked
			listDeclarations();
		} catch (e) {
			console.log(e);
		}
		setLoadingAction(null);
	};

	const handleSubmitClick = async () => {
		if (loadingAction) {
			return;
		}

		const errors = getValidationErrors();

		if (errors.length > 0) {
			return setToast(t("validationErrorToast"));
		}

		setLoadingAction("submit");
		updateQueryParam(AMENDMENT_QUERY_PARAM, "");

		try {
			const result = await submitDeclaration(state.declaration);
			console.log(result);
			let status = "SUCCESS";
			if (result.hmrcResponse.errorStatus) {
				status = "FAILURE";
				setToast(t("submitErrorToast"));
			} else {
				setToast(t("submitSuccessToast"));
			}
			await handleSaveClick(status);
		} catch (e) {
			console.log(e?.response?.data);
			await handleSaveClick("FAILURE");
			setToast(t("submitErrorToast"));
		}

		setLoadingAction(null);
	};

	const handleDuplicateClick = async () => {
		setLoadingAction("duplicate");

		await createDeclaration(``, {
			...state.declaration,
			metadata: {},
		});

		setLoadingAction(null);
		listDeclarations();
	};

	const handleDeleteClick = async () => {
		const { creationTime } = state.declarationMetadata;
		if (loadingAction) {
			return;
		}

		setLoadingAction("delete");
		await deleteDeclaration(creationTime);

		setDeclarations([
			...state.declarations.filter((declaration) => declaration.creationTime !== creationTime),
		]);

		setLoadingAction(null);
		setIsDeleteConfirmModalOpen(false);

		removeQueryParam(CREATION_TIME_QUERY_PARAM);
		clearSelectedDeclaration();
	};

	if (!state.declaration || !state.declarationMetadata) {
		return null;
	}

	return (
		<div className={withTheme("declaration-action-buttons-container")}>
			<NotificationToast open={Boolean(toast)} handleClose={() => setToast("")} text={toast} />

			<CancelDeclarationModal
				isOpen={isCancelConfirmModalOpen}
				onClose={() => setIsCancelConfirmModalOpen(false)}
			/>

			<ConfirmationModal
				isOpen={isDeleteConfirmModalOpen}
				title={t("areYouSure")}
				description={t("deleteDeclarationConfirm")}
				primaryActionLabel={t("deleteDeclaration")}
				isActionLoading={loadingAction === "delete"}
				onPrimaryActionClick={handleDeleteClick}
				onClose={() => setIsDeleteConfirmModalOpen(false)}
			/>

			<Button
				tooltip={t("duplicateDeclaration")}
				isLoading={loadingAction === "duplicate"}
				type="icon"
				onClick={handleDuplicateClick}
			>
				<PiCopy />
			</Button>

			<Button
				tooltip={t("deleteDeclaration")}
				type="icon"
				onClick={() => setIsDeleteConfirmModalOpen(true)}
			>
				<PiTrash />
			</Button>

			<Badge isCompact isHidden={!state.hasUnsavedChanges}>
				<Button
					tooltip={t("saveChanges")}
					isLoading={loadingAction === "save"}
					type="icon"
					onClick={handleSaveClick}
				>
					<PiFloppyDisk />
				</Button>
			</Badge>

			{state.declarationMetadata.status === "SUCCESS" && (
				<>
					{isAmendment && (
						<Button onClick={handleCancelAmendmentClick} LeftIcon={PiX}>
							{t("cancelAmendment")}
						</Button>
					)}

					{!isAmendment && (
						<>
							<Button
								onClick={() => setIsCancelConfirmModalOpen(true)}
								type="secondary"
								LeftIcon={PiX}
							>
								{t("cancelDeclaration")}
							</Button>

							<Button onClick={handleAmendClick} LeftIcon={PiPencilSimple}>
								{t("amend")}
							</Button>
						</>
					)}
				</>
			)}

			{!isAmendment && state.declarationMetadata.status !== "SUCCESS" && (
				<Button
					onClick={handleSubmitClick}
					isLoading={loadingAction === "submit"}
					LeftIcon={PiArrowFatUp}
				>
					{t("submit")}
				</Button>
			)}

			{isAmendment && (
				<Button
					onClick={() => handleSaveClick("SUCCESS", true)}
					isLoading={loadingAction === "amend"}
					LeftIcon={PiArrowFatUp}
				>
					{t("submitAmendment")}
				</Button>
			)}
		</div>
	);
};

export default DeclarationActions;
